import {
    IconCheckCircleFilled,
    IconFileWithItemsOutline,
    IconPlus
} from '@iwoca/icons';
import { Button, Spinner } from '@iwoca/orion';
import classNames from 'classnames';

import styles from './UploadedFiles.module.css';
import { TFile, TFileStatus } from '../hooks/useFileUploadReducer';

const UploadedFiles = ({
    files,
    removeFile
}: {
    files: TFile[];
    removeFile: (id: string) => void;
}) => {
    const renderIcon = (status: TFileStatus) => {
        if (status === 'loading') {
            return (
                <Spinner dataTestId="loading-icon" className={styles.spinner} />
            );
        }

        return (
            <IconFileWithItemsOutline
                data-testid={`${status}-icon`}
                width={20}
                height={20}
                className={classNames(styles.icon, {
                    [styles.errorIcon]: status === 'error',
                    [styles.rejectedIcon]: status === 'rejected'
                })}
            />
        );
    };

    return (
        <>
            {files.map((file) => {
                const hasError = file.status === 'error';
                const isRejected = file.status === 'rejected';

                return (
                    <div
                        key={file.id}
                        data-testid={`uploaded-file-${file.id}`}
                        className={styles.uploadedFiles}
                    >
                        <div className={styles.fileNameContainer}>
                            <div className={styles.iconContainer}>
                                {renderIcon(file.status)}
                            </div>
                            <p
                                className={classNames(styles.name, {
                                    [styles.errored]: hasError,
                                    [styles.rejected]: isRejected
                                })}
                            >
                                {file.fileName}
                            </p>

                            {file.status === 'success' && (
                                <div
                                    className={classNames(
                                        styles.statusContainer,
                                        styles.success
                                    )}
                                >
                                    <IconCheckCircleFilled aria-hidden />
                                    <p>Submitted</p>
                                </div>
                            )}

                            {hasError && (
                                <Button
                                    data-testid="remove-button"
                                    size="small"
                                    variant="tertiary"
                                    onClick={() => removeFile(file.id)}
                                    className={styles.removeButton}
                                >
                                    <IconPlus
                                        height={20}
                                        width={20}
                                        className={styles.removeIcon}
                                    />
                                </Button>
                            )}
                        </div>
                        {file.errorReason && !isRejected && (
                            <div className={styles.errorMessageContainer}>
                                <p className={styles.errorMessage}>
                                    {file.errorReason}
                                </p>
                            </div>
                        )}
                        {!file.errorReason && isRejected && (
                            <div className={styles.errorMessageContainer}>
                                <p
                                    className={classNames(
                                        styles.errorMessage,
                                        styles.rejectedMessage
                                    )}
                                >
                                    {file.rejectReason}
                                </p>
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default UploadedFiles;
